/**
 * Error capture from time zero WITHOUT shipping the Rollbar SDK (~28 KiB gz)
 * in the app chunk: a tiny shim queues window errors immediately, and the real
 * SDK (initRollbar.ts) is dynamically imported on idle — or right away when
 * the first error arrives. On init the queue is flushed to Rollbar.
 */
import type Rollbar from "rollbar";

type QueuedItem =
  | {
      kind: "log";
      message: string;
      error: unknown;
      custom?: Record<string, unknown>;
    }
  | { kind: "uncaught"; event: ErrorEvent }
  | { kind: "rejection"; event: PromiseRejectionEvent };

// Undocumented but stable SDK methods behind captureUncaught and
// captureUnhandledRejections: replaying through them gives early errors the
// same title, grouping and ignoredMessages filtering as later ones.
type RollbarGlobalHandlers = {
  handleUncaughtException(
    message: string,
    url: string,
    lineno: number,
    colno: number,
    error: unknown
  ): void;
  handleUnhandledRejection(reason: unknown, promise: unknown): void;
};

const MAX_QUEUE = 20;
const queue: QueuedItem[] = [];
const readyCallbacks: Array<(rollbar: Rollbar) => void> = [];

let instance: Rollbar | undefined;
let loadPromise: Promise<void> | undefined;
let pendingConfig: Rollbar.Configuration | undefined;

const enqueue = (item: QueuedItem) => {
  if (queue.length < MAX_QUEUE) queue.push(item);
  // An error is the strongest reason to stop waiting for idle.
  if (pendingConfig) loadNow();
};

/** Queue-aware reporter for callers that may run before the SDK is ready. */
export const reportError = (
  message: string,
  error: unknown,
  custom?: Record<string, unknown>
): void => {
  if (instance) instance.error(message, error as Rollbar.LogArgument, custom);
  else enqueue({ kind: "log", message, error, custom });
};

export function installEarlyCapture(): void {
  window.addEventListener("error", (event) => {
    if (instance) return; // Rollbar's own captureUncaught handles it now
    enqueue({ kind: "uncaught", event });
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (instance) return;
    enqueue({ kind: "rejection", event });
  });
}

const flush = (rollbar: Rollbar, item: QueuedItem) => {
  const handlers = rollbar as unknown as RollbarGlobalHandlers;
  switch (item.kind) {
    case "log":
      rollbar.error(
        item.message,
        item.error as Rollbar.LogArgument,
        item.custom
      );
      break;
    case "uncaught": {
      const { message, filename, lineno, colno, error } = item.event;
      handlers.handleUncaughtException(message, filename, lineno, colno, error);
      break;
    }
    case "rejection":
      handlers.handleUnhandledRejection(item.event.reason, item.event.promise);
      break;
  }
};

const loadNow = (): Promise<void> => {
  const config = pendingConfig;
  if (!config) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = import("./initRollbar").then(({ initRollbar }) => {
      instance = initRollbar(config);
      for (const item of queue.splice(0)) flush(instance, item);
      for (const cb of readyCallbacks.splice(0)) cb(instance);
    });
  }
  return loadPromise;
};

/** Load the SDK when the main thread is idle (or on the first queued error). */
export function scheduleRollbarLoad(config: Rollbar.Configuration): void {
  if (!config.enabled) return;
  pendingConfig = config;
  // Lets the inline window.Rollbar stub (gatsby-ssr.tsx) force the SDK load
  // as soon as an external library reports through it.
  window.__rollbarWake = () => void loadNow();
  if (queue.length > 0) {
    void loadNow();
  } else if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => void loadNow(), { timeout: 4000 });
  } else {
    setTimeout(() => void loadNow(), 3000);
  }
}

/**
 * Notifies `cb` once the SDK is initialized (immediately if it already is).
 * Never fires when Rollbar is disabled. Returns an unsubscribe function.
 */
export function whenRollbarReady(cb: (rollbar: Rollbar) => void): () => void {
  if (instance) {
    cb(instance);
    return () => undefined;
  }
  readyCallbacks.push(cb);
  return () => {
    const i = readyCallbacks.indexOf(cb);
    if (i >= 0) readyCallbacks.splice(i, 1);
  };
}
